import React, { useState, useEffect, useCallback, useMemo, FC } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useParams,
  useNavigate,
  useLocation,
  Link,
  Navigate,
} from 'react-router-dom';
import { useMobileNav } from '../contexts/MobileNavContext';
import {
  TopUpProduct,
  PurchaseTopUpRequest,
  PurchaseTopUpResponse,
  GetAutoTopupSettingsResponse,
  UpsertAutoTopupRequest,
  UpsertAutoTopupResponse,
  ChangePlanRequest,
  ChangePlanResponse,
} from '@dreamai/types';
import { PlanOverviewResponse } from '@dreamai/types';
import { isV3PlanOverview } from '../utils/planOverview';
import { usePlanModal } from '../providers';
import { useAgent } from '../contexts/AgentContext';
import api from '../api';
import CancelPlanModal from './settings/CancelPlanModal';
import PersonalPlanBilling from './settings/PersonalPlanBilling';
import TopUpModal from './settings/TopUpModal';
import AutoTopupSection from './settings/AutoTopupSection';
import YourDetailsSection from './settings/YourDetailsSection';
import TravelTimezoneSection from './settings/TravelTimezoneSection';
import SecuritySection from './settings/SecuritySection';
import ConnectedAccountsSection from './settings/ConnectedAccountsSection';
import PaymentAndInvoices from './settings/PaymentAndInvoices';
import PurchasePotSection from './settings/PurchasePotSection';
import DailyBriefingSection from './settings/DailyBriefingSection';
import EveningSweepSection from './settings/EveningSweepSection';
import SchedulingContactsSection from './settings/SchedulingContactsSection';
import OrganisationSection from './settings/OrganisationSection';
import { useCompany } from '../contexts/CompanyContext';
import { useOrganisationsEnabled } from '../hooks/useOrganisationsEnabled';
import MemoryArea from './chat/MemoryArea';
import DreamIcon from './common/DreamMarque';
import { ReactComponent as DreamLogo } from '../icons/brand/dream-logo-day-ink.svg';
import { ReactComponent as SettingsNavIcon } from '../icons/nav-links/settings.svg';
import { ReactComponent as WhatsappIcon } from '../icons/whatsapp-color.svg';
import { ReactComponent as LogoutIcon } from '../icons/nav-links/logout.svg';
import { ReactComponent as InboxIcon } from '../icons/inbox-01.svg';
import InboxManagementPage from './chat/InboxManagementPage';
import { useAgentWhatsappNumber } from '../hooks/useAgentWhatsappNumber';
import {
  getSettingsReturnTo,
  SettingsLocationState,
} from '../utils/settingsReturn';
import './settings/settings-v2.scss';

// ── Inline SVG icons (matching Figma) ──────────────────────────────────

// nav-links/memory.svg redrawn at the 20px / 2px-stroke scale of the icons below.
const MemoryIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M16.5 13.0005C19.0768 13.0005 21.2397 11.2285 21.8366 8.83653C21.4087 8.94336 20.961 9.00007 20.5 9.00007C17.4624 9.00007 15 6.53763 15 3.50007C15 3.0393 15.0567 2.59177 15.1634 2.16406C12.7717 2.76117 11 4.92394 11 7.50052C11 8.41324 11.2223 9.27404 11.6158 10.0317M6 22.0005C3.79086 22.0005 2 20.2096 2 18.0005C2 16.0226 3.43551 14.3801 5.32148 14.0578C6.12876 11.6975 8.3662 10.0005 11 10.0005C13.2882 10.0005 15.2772 11.2814 16.2892 13.1653C16.6744 13.0579 17.0805 13.0005 17.5 13.0005C19.9853 13.0005 22 15.0152 22 17.5005C22 19.9858 19.9853 22.0005 17.5 22.0005C13.6667 22.0005 9.83333 22.0005 6 22.0005Z'
      stroke='currentColor'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const UserIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <g clipPath='url(#clip0_1162_9158)'>
      <path
        d='M3.33368 18.1805C3.83583 18.3327 4.51407 18.3327 5.66699 18.3327H14.3337C15.4866 18.3327 16.1648 18.3327 16.667 18.1805M3.33368 18.1805C3.22601 18.1479 3.12644 18.1083 3.03202 18.0602C2.56161 17.8205 2.17916 17.4381 1.93948 16.9677C1.66699 16.4329 1.66699 15.7328 1.66699 14.3327V5.66602C1.66699 4.26588 1.66699 3.56582 1.93948 3.03104C2.17916 2.56063 2.56161 2.17818 3.03202 1.9385C3.5668 1.66602 4.26686 1.66602 5.66699 1.66602H14.3337C15.7338 1.66602 16.4339 1.66602 16.9686 1.9385C17.439 2.17818 17.8215 2.56063 18.0612 3.03104C18.3337 3.56582 18.3337 4.26588 18.3337 5.66602V14.3327C18.3337 15.7328 18.3337 16.4329 18.0612 16.9677C17.8215 17.4381 17.439 17.8205 16.9686 18.0602C16.8742 18.1083 16.7746 18.1479 16.667 18.1805M3.33368 18.1805C3.33395 17.5061 3.338 17.1492 3.39771 16.849C3.66073 15.5267 4.69439 14.4931 6.01669 14.2301C6.33869 14.166 6.7259 14.166 7.50033 14.166H12.5003C13.2748 14.166 13.662 14.166 13.984 14.2301C15.3063 14.4931 16.3399 15.5267 16.6029 16.849C16.6627 17.1492 16.6667 17.5061 16.667 18.1805M13.3337 7.91602C13.3337 9.75696 11.8413 11.2493 10.0003 11.2493C8.15938 11.2493 6.66699 9.75696 6.66699 7.91602C6.66699 6.07507 8.15938 4.58268 10.0003 4.58268C11.8413 4.58268 13.3337 6.07507 13.3337 7.91602Z'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_1162_9158'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const ShoppingBagIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M4.6 2.19935L3.3 3.93268C3.04251 4.276 2.91377 4.44765 2.91676 4.59134C2.91936 4.71638 2.97799 4.83364 3.07646 4.91074C3.18962 4.99935 3.40419 4.99935 3.83333 4.99935H16.1667C16.5958 4.99935 16.8104 4.99935 16.9235 4.91074C17.022 4.83364 17.0806 4.71638 17.0832 4.59134C17.0862 4.44765 16.9575 4.276 16.7 3.93268L15.4 2.19935M4.6 2.19935C4.74667 2.00379 4.82 1.90602 4.91294 1.8355C4.99525 1.77304 5.08846 1.72643 5.18782 1.69805C5.3 1.66602 5.42222 1.66602 5.66667 1.66602H14.3333C14.5778 1.66602 14.7 1.66602 14.8122 1.69805C14.9115 1.72643 15.0047 1.77304 15.0871 1.8355C15.18 1.90602 15.2533 2.00379 15.4 2.19935M4.6 2.19935L3.03333 4.28824C2.83545 4.55208 2.73651 4.684 2.66625 4.82928C2.6039 4.9582 2.55843 5.09461 2.53096 5.23515C2.5 5.39353 2.5 5.55843 2.5 5.88824L2.5 15.666C2.5 16.5994 2.5 17.0661 2.68166 17.4227C2.84144 17.7363 3.09641 17.9912 3.41002 18.151C3.76654 18.3327 4.23325 18.3327 5.16667 18.3327L14.8333 18.3327C15.7668 18.3327 16.2335 18.3327 16.59 18.151C16.9036 17.9912 17.1586 17.7363 17.3183 17.4227C17.5 17.0661 17.5 16.5994 17.5 15.666V5.88824C17.5 5.55843 17.5 5.39353 17.469 5.23515C17.4416 5.09461 17.3961 4.9582 17.3338 4.82929C17.2635 4.68401 17.1646 4.55208 16.9667 4.28824L15.4 2.19935M13.3333 8.33268C13.3333 9.21674 12.9821 10.0646 12.357 10.6897C11.7319 11.3148 10.8841 11.666 10 11.666C9.11594 11.666 8.2681 11.3148 7.64298 10.6897C7.01786 10.0646 6.66667 9.21674 6.66667 8.33268'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LinkIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M8.33301 10.8327C8.69088 11.3109 9.14747 11.7071 9.67196 11.9939C10.1965 12.2807 10.7762 12.4513 11.3719 12.4938C11.9676 12.5363 12.5651 12.4497 13.1239 12.2403C13.6826 12.0308 14.1889 11.7032 14.6088 11.2802L17.1088 8.78024C17.8636 7.99774 18.2793 6.94524 18.2693 5.85274C18.2593 4.76024 17.8244 3.71574 17.055 2.94724C16.2857 2.17874 15.2408 1.74474 14.1483 1.73574C13.0558 1.72674 12.0038 2.14324 11.2213 2.89857L9.78967 4.32191M11.6663 9.16607C11.3085 8.68791 10.8519 8.29171 10.3274 8.0049C9.80289 7.71808 9.22318 7.54751 8.62749 7.50499C8.0318 7.46246 7.43427 7.54907 6.87553 7.75853C6.31679 7.96799 5.81046 8.29559 5.39051 8.71857L2.89051 11.2186C2.13576 12.0011 1.72009 13.0536 1.73009 14.1461C1.7401 15.2386 2.175 16.2831 2.94434 17.0516C3.71368 17.8201 4.75867 18.2541 5.85117 18.2631C6.94367 18.2721 7.99559 17.8556 8.77801 17.1002L10.2013 15.6769'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CoinsIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M13.2814 13.2814C16.1336 12.8996 18.3333 10.4567 18.3333 7.5C18.3333 4.27834 15.7216 1.66667 12.5 1.66667C9.54328 1.66667 7.10037 3.8664 6.71852 6.71856M6.24996 10.8333L7.49996 10V14.5833M6.24996 14.5833H8.74996M13.3333 12.5C13.3333 15.7217 10.7216 18.3333 7.49996 18.3333C4.2783 18.3333 1.66663 15.7217 1.66663 12.5C1.66663 9.27834 4.2783 6.66667 7.49996 6.66667C10.7216 6.66667 13.3333 9.27834 13.3333 12.5Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CertificateIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M9 18.5H15M7 15H17M5 2H19C20.1046 2 21 2.99492 21 4.22222V19.7778C21 21.0051 20.1046 22 19 22H5C3.89543 22 3 21.0051 3 19.7778V4.22222C3 2.99492 3.89543 2 5 2ZM11.9976 6.21194C11.2978 5.4328 10.1309 5.22321 9.25414 5.93667C8.37738 6.65013 8.25394 7.84299 8.94247 8.6868C9.631 9.53061 11.9976 11.5 11.9976 11.5C11.9976 11.5 14.3642 9.53061 15.0527 8.6868C15.7413 7.84299 15.6329 6.64262 14.7411 5.93667C13.8492 5.23072 12.6974 5.4328 11.9976 6.21194Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CoinsHandIcon: FC = () => (
  <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
    <path
      d='M11.7844 14.1L9.35 12.15C9.05 11.9 8.65 11.75 8.25 11.75H7.05M7.05 11.75C7.35 11.05 8.05 10.55 8.85 10.55H11.35C12.55 10.55 13.65 10.95 14.55 11.65L16.35 13.05C16.85 13.45 17.55 13.45 18.05 13.05C18.65 12.55 19.55 12.6 20.1 13.15L22 15.05M7.05 11.75H2V19.75H7.05C7.05 19.75 11.85 21.75 14.05 21.75C16.25 21.75 22 18.75 22 18.75C22.6 18.35 22.75 17.55 22.35 16.95C21.95 16.4 21.25 16.25 20.65 16.55L17.05 18.35M13.05 7.25C13.05 8.90685 11.7069 10.25 10.05 10.25C8.39315 10.25 7.05 8.90685 7.05 7.25C7.05 5.59315 8.39315 4.25 10.05 4.25C11.7069 4.25 13.05 5.59315 13.05 7.25ZM20.05 5.25C20.05 6.90685 18.7069 8.25 17.05 8.25C15.3931 8.25 14.05 6.90685 14.05 5.25C14.05 3.59315 15.3931 2.25 17.05 2.25C18.7069 2.25 20.05 3.59315 20.05 5.25Z'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LockIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M14.1667 9.16667V6.66667C14.1667 4.36548 12.3012 2.5 10 2.5C7.69882 2.5 5.83334 4.36548 5.83334 6.66667V9.16667M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V13.1667C17.5 11.7665 17.5 11.0665 17.2275 10.5317C16.9878 10.0613 16.6054 9.67883 16.135 9.43915C15.6002 9.16667 14.9001 9.16667 13.5 9.16667H6.5C5.09987 9.16667 4.3998 9.16667 3.86502 9.43915C3.39462 9.67883 3.01217 10.0613 2.77248 10.5317C2.5 11.0665 2.5 11.7665 2.5 13.1667V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const SunriseIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 2V4M4.92893 5.92893L6.34315 7.34315M19.0711 5.92893L17.6569 7.34315M2 17H4M20 17H22M7 17C7 14.2386 9.23858 12 12 12C14.7614 12 17 14.2386 17 17M2 21H22'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const OrganisationIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M22 21V19C22 17.1362 20.7252 15.5701 19 15.126M15.5 3.29076C16.9659 3.88415 18 5.32131 18 7C18 8.67869 16.9659 10.1159 15.5 10.7092M17 21C17 19.1362 17 18.2044 16.6955 17.4693C16.2895 16.4892 15.5108 15.7105 14.5307 15.3045C13.7956 15 12.8638 15 11 15H8C6.13623 15 5.20435 15 4.46927 15.3045C3.48915 15.7105 2.71046 16.4892 2.30448 17.4693C2 18.2044 2 19.1362 2 21M13.5 7C13.5 9.20914 11.7091 11 9.5 11C7.29086 11 5.5 9.20914 5.5 7C5.5 4.79086 7.29086 3 9.5 3C11.7091 3 13.5 4.79086 13.5 7Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CalendarUsersIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5ZM12 13.5C13.1046 13.5 14 12.6046 14 11.5C14 10.3954 13.1046 9.5 12 9.5C10.8954 9.5 10 10.3954 10 11.5C10 12.6046 10.8954 13.5 12 13.5ZM15.5 18.5C15.5 16.567 13.933 15.5 12 15.5C10.067 15.5 8.5 16.567 8.5 18.5'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ChevronRightIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M6 4L10 8L6 12'
      stroke='#838B9B'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ArrowLeftIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M16.6668 10H3.3335M3.3335 10L10.0002 16.6667M3.3335 10L10.0002 3.33333'
      stroke='#0d1f16'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// ── Helpers ─────────────────────────────────────────────────────────────

function formatCredits(credits: number): string {
  return Math.floor(credits).toLocaleString('en-GB');
}

function formatPrice(amount: number, currency: string): string {
  const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
  const sym = symbols[currency] || currency + ' ';
  const withTax = Math.round(amount * 1.2 * 100) / 100;
  const display = withTax.toFixed(2).replace(/\.00$/, '');
  return `${sym}${display}`;
}

function formatResetDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// ── Component ──────────────────────────────────────────────────────────

type SettingsTab =
  | 'memories'
  | 'your-details'
  | 'security'
  | 'connected-accounts'
  | 'inbox-manager'
  | 'plan-and-topups'
  | 'purchase-pot'
  | 'daily-briefing'
  | 'scheduling-contacts'
  | 'organisation';

const SETTING_TO_TAB: Record<string, SettingsTab> = {
  memories: 'memories',
  'your-details': 'your-details',
  security: 'security',
  'connected-accounts': 'connected-accounts',
  'inbox-manager': 'inbox-manager',
  'plan-and-topups': 'plan-and-topups',
  'billing-details': 'plan-and-topups',
  'purchase-pot': 'purchase-pot',
  notifications: 'your-details',
  'daily-briefing': 'daily-briefing',
  'scheduling-contacts': 'scheduling-contacts',
  organisation: 'organisation',
};

const MOBILE_NAV_ITEMS: {
  key: SettingsTab;
  label: string;
  icon: FC;
}[] = [
  { key: 'memories', label: 'Memories', icon: MemoryIcon },
  { key: 'your-details', label: 'Your Details', icon: UserIcon },
  { key: 'security', label: 'Your Account', icon: LockIcon },
  { key: 'connected-accounts', label: 'Connected Apps', icon: LinkIcon },
  { key: 'inbox-manager', label: 'Inbox Manager', icon: InboxIcon },
  { key: 'plan-and-topups', label: 'Plan & billing', icon: ShoppingBagIcon },
  { key: 'purchase-pot', label: 'Secretary’s Card', icon: CoinsIcon },
  { key: 'daily-briefing', label: 'Daily Briefing', icon: SunriseIcon },
  {
    key: 'scheduling-contacts',
    label: 'Your Circle',
    icon: CalendarUsersIcon,
  },
  { key: 'organisation', label: 'Organisation', icon: OrganisationIcon },
];

const MOBILE_TAB_LABELS: Record<string, string> = {
  memories: 'Memories',
  'your-details': 'Your Details',
  security: 'Your Account',
  'connected-accounts': 'Connected Apps',
  'inbox-manager': 'Inbox Manager',
  'plan-and-topups': 'Plan & billing',
  'purchase-pot': 'Secretary’s Card',
  'daily-briefing': 'Daily Briefing',
  'scheduling-contacts': 'Your Circle',
  organisation: 'Organisation',
};

const Settings: React.FC = () => {
  const { setting } = useParams<{ setting?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the mobile Back leaves settings to: the view that linked here
  // (carried through sub-page navigation), or home when opened directly.
  const returnTo = getSettingsReturnTo(location.state);
  const returnState: SettingsLocationState = { returnTo };
  const { isMobile } = useMobileNav();
  const { whatsappLink } = useAgentWhatsappNumber();
  const { getAccessTokenSilently, logout } = useAuth0();
  const { openPlanModal } = usePlanModal();
  const { planOverview: companyPlanOverview, refreshPlanOverview } =
    useCompany();
  const { agents } = useAgent();
  const paAgent = agents.find(
    (a) => (a.agent_type as string) === 'Personal Assistant',
  );
  const hasPA = !!paAgent;
  const paName = paAgent?.nickname || 'Secretary';
  // The Organisation page exists only when organisations are switched on
  // for this user.
  const organisationsEnabled = useOrganisationsEnabled();

  const assistantName = useMemo(() => {
    const pa = agents.find(
      (a) => (a.agent_type as string) === 'Personal Assistant',
    );
    return pa?.nickname || pa?.name || 'Secretary';
  }, [agents]);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const getMobileLabel = useCallback(
    (key: string, fallback: string) => {
      if (key === 'purchase-pot') return `${assistantName}'s Card`;
      return fallback;
    },
    [assistantName],
  );

  const defaultTab: SettingsTab = 'your-details';
  const resolvedTab = setting ? SETTING_TO_TAB[setting] : undefined;
  // The open page comes from the URL, so it can be linked to and reloaded.
  const activeTab: SettingsTab =
    resolvedTab && resolvedTab !== 'memories' ? resolvedTab : defaultTab;

  // A tab the user cannot see (deep link, or the flag changed underneath
  // them) falls back to the default one once the gate has answered.
  const isTabAvailable = useCallback(
    (tab: SettingsTab): boolean | null => {
      if (tab === 'organisation') return organisationsEnabled;
      return true;
    },
    [organisationsEnabled],
  );
  const openTab = useCallback(
    (tab: SettingsTab) => {
      const state: SettingsLocationState = { returnTo };
      navigate(`/settings/${tab}`, { state });
    },
    [navigate, returnTo],
  );
  useEffect(() => {
    document.body.classList.add('settings-canvas-bg');
    return () => document.body.classList.remove('settings-canvas-bg');
  }, []);
  useEffect(() => {
    if (resolvedTab && isTabAvailable(resolvedTab) === false) {
      const state: SettingsLocationState = { returnTo };
      navigate(isMobile ? '/settings' : `/settings/${defaultTab}`, {
        replace: true,
        state,
      });
    }
  }, [isMobile, resolvedTab, isTabAvailable, navigate, returnTo]);

  const [overview, setOverview] = useState<PlanOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [restoringPlan, setRestoringPlan] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [purchasingTopUp, setPurchasingTopUp] = useState<string | null>(null);
  const [topUpSuccess, setTopUpSuccess] = useState<string | null>(null);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [topUpModalProduct, setTopUpModalProduct] =
    useState<TopUpProduct | null>(null);
  const [autoTopupEnabled, setAutoTopupEnabled] = useState(false);
  const [autoTopupRefreshKey, setAutoTopupRefreshKey] = useState(0);
  const [startingSubscription, setStartingSubscription] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const v3Overview = isV3PlanOverview(overview) ? overview : null;
  const legacyOverview = isV3PlanOverview(overview) ? null : overview;
  // Connected Apps renders its own Back button inside its per-app detail
  // views — hide the settings-level Back pill while one is open on mobile.
  const [caDetailOpen, setCaDetailOpen] = useState(false);
  const handleCaViewChange = useCallback((view: string) => {
    setCaDetailOpen(view !== 'list');
  }, []);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<PlanOverviewResponse>(
        '/credits/plan-overview',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOverview(res.data);
      if (isV3PlanOverview(res.data)) return;

      try {
        const autoRes = await api.get<GetAutoTopupSettingsResponse>(
          '/credits/auto-topup',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAutoTopupEnabled(
          autoRes.data.settings.some(
            (s) => s.creditType === 'credit' && s.enabled,
          ),
        );
      } catch {
        // Non-fatal — the opt-in just stays visible
      }
    } catch (err) {
      console.error('Error fetching plan overview:', err);
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleCancelPlan = useCallback(async () => {
    if (cancellingPlan) return;
    setCancellingPlan(true);
    setCancelError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/credits/cancel-plan',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowCancelModal(false);
      await fetchOverview();
      refreshPlanOverview();
    } catch (err) {
      console.error('Error cancelling plan:', err);
      setCancelError('Something went wrong. Please try again.');
    } finally {
      setCancellingPlan(false);
    }
  }, [
    cancellingPlan,
    getAccessTokenSilently,
    fetchOverview,
    refreshPlanOverview,
  ]);

  const handleRestorePlan = useCallback(async () => {
    if (restoringPlan) return;
    setRestoringPlan(true);
    setCancelError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/credits/restore-plan',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchOverview();
      refreshPlanOverview();
    } catch (err) {
      console.error('Error restoring plan:', err);
      setCancelError('Something went wrong. Please try again.');
    } finally {
      setRestoringPlan(false);
    }
  }, [
    restoringPlan,
    getAccessTokenSilently,
    fetchOverview,
    refreshPlanOverview,
  ]);

  // Billing v3: the single personal plan goes straight to Airwallex checkout.
  const handleStartSubscription = useCallback(async () => {
    if (startingSubscription || !v3Overview?.offer) return;
    setStartingSubscription(true);
    setStartError(null);
    try {
      const token = await getAccessTokenSilently();
      const body: ChangePlanRequest = {
        newPriceId: v3Overview.offer.priceId,
      };
      const res = await api.post<ChangePlanResponse>(
        '/credits/change-plan',
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      console.error('Error starting subscription:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const code = axiosErr.response?.data?.error ?? null;
      setStartError(
        code === 'covered_by_organisation'
          ? "Your Dream use is covered by your organisation's subscription."
          : code === 'plan_override'
            ? 'Your Dream use is already unlimited.'
            : 'Something went wrong. Please try again.',
      );
      setStartingSubscription(false);
    }
  }, [startingSubscription, v3Overview, getAccessTokenSilently]);

  const openTopUpModal = useCallback((topUp: TopUpProduct) => {
    setTopUpSuccess(null);
    setTopUpError(null);
    setTopUpModalProduct(topUp);
  }, []);

  const handleTopUpConfirm = useCallback(
    async (
      topUp: TopUpProduct,
      quantity: number,
      autoTopupCap: number | null,
    ) => {
      if (purchasingTopUp) return;
      setPurchasingTopUp(topUp.priceId);
      setTopUpError(null);

      try {
        const token = await getAccessTokenSilently();
        const body: PurchaseTopUpRequest = {
          priceId: topUp.priceId,
          quantity,
        };
        const res = await api.post<PurchaseTopUpResponse>(
          '/credits/purchase-topup',
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (autoTopupCap !== null) {
          try {
            const autoBody: UpsertAutoTopupRequest = {
              creditType: 'credit',
              enabled: true,
              monthlyBudgetCap: autoTopupCap,
              balanceThreshold: 1000,
              topupPriceId: topUp.priceId,
              topupQuantity: 1,
            };
            await api.put<UpsertAutoTopupResponse>(
              '/credits/auto-topup',
              autoBody,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setAutoTopupRefreshKey((k) => k + 1);
          } catch (autoErr) {
            console.error('Error enabling auto top-up:', autoErr);
          }
        }

        setTopUpModalProduct(null);
        setTopUpSuccess(
          `Added ${formatCredits(res.data.unitsAdded)} credits to your account.`,
        );
        await fetchOverview();
      } catch (err) {
        console.error('Error purchasing top-up:', err);
        setTopUpError('Failed to purchase top-up. Please try again.');
      } finally {
        setPurchasingTopUp(null);
      }
    },
    [purchasingTopUp, getAccessTokenSilently, fetchOverview],
  );

  // ── Render ──────────────────────────────────────────────────────────

  const renderPlanAndTopUps = () => {
    if (loading) {
      return (
        <>
          <h1 className='plan-page-title'>
            {isV3PlanOverview(companyPlanOverview)
              ? 'Plan & billing'
              : 'Plan & Top-Ups'}
          </h1>
          <div className='settings-skeleton' />
        </>
      );
    }

    if (error || !overview) {
      return (
        <div className='settings-error'>{error || 'Something went wrong.'}</div>
      );
    }

    if (isV3PlanOverview(overview)) {
      return (
        <>
          <PersonalPlanBilling
            overview={overview}
            onStartSubscription={handleStartSubscription}
            startingSubscription={startingSubscription}
            onCancelSubscription={() => setShowCancelModal(true)}
            onRestoreSubscription={handleRestorePlan}
            restoringSubscription={restoringPlan}
            error={startError || cancelError}
          />
          <PaymentAndInvoices
            showPaymentMethod={overview.source !== 'organisation_seat'}
            showNextBilling={false}
          />
        </>
      );
    }

    const {
      hasSubscription,
      plan,
      credits,
      resetsAt,
      cancellingAt,
      topUpProducts,
    } = overview;

    // ── Plan section ──────────────────────────────────────────────────
    const planCredits = hasSubscription ? (plan?.credits ?? 0) : 10000;
    const subscriptionRemaining = hasSubscription
      ? credits.subscriptionCredits
      : credits.totalCredits;
    const progressPct =
      planCredits > 0
        ? Math.max(
            0,
            Math.min(100, (subscriptionRemaining / planCredits) * 100),
          )
        : 0;

    const creditTopUp = topUpProducts.find((t) => t.creditType === 'credit');

    return (
      <>
        <div className='plan-page-header'>
          <h1 className='plan-page-title'>Plan &amp; Top-Ups</h1>
        </div>

        {/* ── Subscription ──────────────────────────────────── */}
        <div className='plan-card subscription-card'>
          <h2 className='plan-card-title'>Your subscription</h2>

          <div className='subscription-summary'>
            <div className='subscription-headline'>
              <div className='subscription-plan'>
                <span className='subscription-plan-name'>
                  {hasSubscription ? plan?.name || 'Active' : 'Trial'}
                </span>
                <span className='subscription-plan-price'>
                  {hasSubscription && plan
                    ? `${formatPrice(plan.amount, plan.currency)}/${plan.periodUnit.toLowerCase()}`
                    : 'Free credits included'}
                </span>
              </div>
              <div className='subscription-credits-count'>
                <CoinsIcon />
                <span>
                  <strong>{formatCredits(subscriptionRemaining)}</strong>
                  {`/${formatCredits(planCredits)} credits remaining`}
                </span>
              </div>
            </div>

            <div className='subscription-progress'>
              <div
                className='subscription-progress-fill'
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {hasSubscription && cancellingAt ? (
              <div className='subscription-caption cancelling'>
                <p>
                  Your plan will cancel on {formatResetDate(cancellingAt)}. You
                  can continue using Dream normally until then.
                </p>
                <p>
                  Automatic top-ups will be disabled after your cancellation
                  date.
                </p>
              </div>
            ) : hasSubscription && resetsAt ? (
              <p className='subscription-caption'>
                Your allowance resets on {formatResetDate(resetsAt)}
              </p>
            ) : null}
          </div>

          {cancelError && <p className='topup-feedback error'>{cancelError}</p>}

          <div className='subscription-actions'>
            {hasSubscription && cancellingAt ? (
              <button
                className='btn-plan-primary'
                onClick={handleRestorePlan}
                disabled={restoringPlan}
              >
                {restoringPlan ? 'Restoring...' : 'Restore your paid plan'}
              </button>
            ) : (
              <>
                <button
                  className='btn-plan-primary'
                  onClick={() => openPlanModal(plan?.priceId)}
                >
                  {hasSubscription ? 'Change your plan' : 'Choose your plan'}
                </button>
                {hasSubscription && (
                  <button
                    className='btn-plan-cancel'
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Top-ups ────────────────────────────────────────── */}
        <div className='plan-card topup-card'>
          <div className='plan-card-header'>
            <h2 className='plan-card-title'>Top-up my account</h2>
            {hasSubscription && (
              <p className='plan-card-subtitle'>
                Your top-ups will roll over to following months and are used
                after your subscription time.
              </p>
            )}
          </div>

          {topUpSuccess && (
            <p className='topup-feedback success'>{topUpSuccess}</p>
          )}
          {topUpError && <p className='topup-feedback error'>{topUpError}</p>}

          {hasSubscription ? (
            <div className='topup-balance-row'>
              <p className='topup-balance-label'>
                Top-up balance:{' '}
                <strong>{formatCredits(credits.topUpCredits)} credits</strong>
              </p>
              {creditTopUp && (
                <button
                  className='btn-plan-primary'
                  onClick={() => openTopUpModal(creditTopUp)}
                  disabled={purchasingTopUp !== null}
                >
                  Add top-up
                </button>
              )}
            </div>
          ) : (
            <div className='topup-disabled'>
              <CoinsHandIcon />
              <p>Upgrade to a paid plan to enable top-ups</p>
            </div>
          )}
        </div>

        {/* ── Auto top-up ──────────────────────────────────── */}
        <AutoTopupSection
          key={autoTopupRefreshKey}
          topUpProducts={topUpProducts}
          hasSubscription={hasSubscription}
        />

        <PaymentAndInvoices showPaymentMethod />
      </>
    );
  };

  // ── Mobile: determine active sub-page from URL param ────────────────
  const mobileSubPage = setting && SETTING_TO_TAB[setting] ? setting : null;

  // Mobile top nav (Figma 4794:66446 — Dream mark left, settings +
  // WhatsApp actions right), shared by the root menu and sub-pages.
  const renderMobileTopNav = () => (
    <div className='settings-mobile-topnav'>
      <Link to='/' className='settings-mobile-topnav-logo' aria-label='Home'>
        <DreamIcon />
      </Link>
      <div className='settings-mobile-topnav-actions'>
        <Link
          to='/settings'
          state={returnState}
          className='settings-mobile-topnav-btn'
          aria-label='Settings'
        >
          <SettingsNavIcon />
        </Link>
        {whatsappLink && (
          <a
            href={whatsappLink}
            target='_blank'
            rel='noreferrer'
            className='settings-mobile-topnav-btn'
            aria-label='Chat on WhatsApp'
          >
            <WhatsappIcon />
          </a>
        )}
      </div>
    </div>
  );

  const renderMobileContent = () => {
    if (!mobileSubPage) {
      // ── Mobile root menu ──────────────────────────────────────────
      return (
        <div className='settings-mobile-root'>
          {renderMobileTopNav()}
          <div className='settings-mobile-header'>
            <button
              className='settings-mobile-back-btn'
              onClick={() => navigate(returnTo)}
            >
              <ArrowLeftIcon />
              <span>Back</span>
            </button>
            <h1 className='settings-mobile-title'>Settings</h1>
          </div>
          <nav className='settings-mobile-nav'>
            {MOBILE_NAV_ITEMS.map(({ key, label, icon: Icon }) => {
              if (
                !hasPA &&
                (key === 'plan-and-topups' || key === 'purchase-pot')
              ) {
                return null;
              }
              if (isTabAvailable(key) !== true) return null;
              return (
                <button
                  key={key}
                  className='settings-mobile-nav-item'
                  onClick={() =>
                    navigate(`/settings/${key}`, { state: returnState })
                  }
                >
                  <Icon />
                  <span className='settings-mobile-nav-label'>
                    {getMobileLabel(key, label)}
                  </span>
                  <ChevronRightIcon />
                </button>
              );
            })}
          </nav>
          <div className='settings-mobile-logout-footer'>
            <button className='settings-mobile-logout' onClick={handleLogout}>
              <span>Log out</span>
              <LogoutIcon />
            </button>
          </div>
        </div>
      );
    }

    // Sections that render their own heading, so the content block carries
    // the title spacing instead.
    const hasInlineTitle =
      mobileSubPage === 'connected-accounts' ||
      mobileSubPage === 'purchase-pot' ||
      mobileSubPage === 'your-details' ||
      mobileSubPage === 'security' ||
      mobileSubPage === 'scheduling-contacts' ||
      mobileSubPage === 'organisation';

    // ── Mobile sub-page ──────────────────────────────────────────────
    return (
      <div className='settings-mobile-subpage'>
        {renderMobileTopNav()}
        {!(mobileSubPage === 'connected-accounts' && caDetailOpen) && (
          <div className='settings-mobile-back-header'>
            <button
              className='settings-mobile-back-btn'
              onClick={() => navigate('/settings', { state: returnState })}
            >
              <ArrowLeftIcon />
              <span>Back</span>
            </button>
          </div>
        )}
        {!hasInlineTitle && (
          <div className='settings-mobile-subpage-title'>
            <h1>
              {getMobileLabel(
                mobileSubPage,
                MOBILE_TAB_LABELS[mobileSubPage] || 'Settings',
              )}
            </h1>
          </div>
        )}
        <div
          className={`settings-mobile-subpage-content${
            hasInlineTitle ? ' has-inline-title' : ''
          }`}
        >
          {mobileSubPage === 'memories' && <MemoryArea embedded />}
          {mobileSubPage === 'plan-and-topups' && renderPlanAndTopUps()}
          {mobileSubPage === 'your-details' && (
            <>
              <YourDetailsSection />
              <TravelTimezoneSection />
            </>
          )}
          {mobileSubPage === 'security' && <SecuritySection />}
          {mobileSubPage === 'connected-accounts' && (
            <ConnectedAccountsSection onViewChange={handleCaViewChange} />
          )}
          {mobileSubPage === 'inbox-manager' && (
            <InboxManagementPage hideHeading />
          )}
          {mobileSubPage === 'purchase-pot' && (
            <PurchasePotSection assistantName={paName} />
          )}
          {mobileSubPage === 'daily-briefing' && (
            <>
              <DailyBriefingSection />
              <EveningSweepSection />
            </>
          )}
          {mobileSubPage === 'scheduling-contacts' && (
            <SchedulingContactsSection assistantName={paName} />
          )}
          {mobileSubPage === 'organisation' &&
            organisationsEnabled === true && <OrganisationSection />}
        </div>
      </div>
    );
  };

  // Memories is a settings sub-page on mobile only; desktop has its own page.
  if (!isMobile && setting === 'memories') {
    return <Navigate to='/memory' replace />;
  }

  // Billing Details was folded into Plan & billing; deep links (the
  // assistant's settings links, the Airwallex card-checkout return URL) keep
  // their query string and router state.
  if (setting === 'billing-details') {
    return (
      <Navigate
        to={{ pathname: '/settings/plan-and-topups', search: location.search }}
        state={location.state}
        replace
      />
    );
  }

  return (
    <div className='settings-v2-wrapper inset-white-backed'>
      {isMobile ? (
        renderMobileContent()
      ) : (
        <div className='settings-v2'>
          <aside className='settings-sidebar'>
            <Link to='/' className='settings-logo' aria-label='Home'>
              <DreamLogo />
            </Link>
            <Link to='/' className='settings-back-link'>
              <ArrowLeftIcon />
              <span>Back</span>
            </Link>
            <h1 className='settings-page-title'>Settings</h1>
            <nav className='settings-nav'>
              <button
                className={`nav-item${activeTab === 'your-details' ? ' active' : ''}`}
                onClick={() => openTab('your-details')}
              >
                <UserIcon />
                <span>Your details</span>
              </button>
              <button
                className={`nav-item${activeTab === 'security' ? ' active' : ''}`}
                onClick={() => openTab('security')}
              >
                <LockIcon />
                <span>Your Account</span>
              </button>
              <button
                className={`nav-item${activeTab === 'connected-accounts' ? ' active' : ''}`}
                onClick={() => openTab('connected-accounts')}
              >
                <LinkIcon />
                <span>Connected Apps</span>
              </button>
              <button
                className={`nav-item${activeTab === 'inbox-manager' ? ' active' : ''}`}
                onClick={() => openTab('inbox-manager')}
              >
                <InboxIcon />
                <span>Inbox Manager</span>
              </button>
              {hasPA && (
                <button
                  className={`nav-item${activeTab === 'plan-and-topups' ? ' active' : ''}`}
                  onClick={() => openTab('plan-and-topups')}
                >
                  <ShoppingBagIcon />
                  <span>Plan & billing</span>
                </button>
              )}
              {hasPA && (
                <button
                  className={`nav-item${activeTab === 'purchase-pot' ? ' active' : ''}`}
                  onClick={() => openTab('purchase-pot')}
                >
                  <CoinsIcon />
                  <span>{paName}&rsquo;s Card</span>
                </button>
              )}
              <button
                className={`nav-item${activeTab === 'daily-briefing' ? ' active' : ''}`}
                onClick={() => openTab('daily-briefing')}
              >
                <SunriseIcon />
                <span>Daily Briefing</span>
              </button>
              <button
                className={`nav-item${activeTab === 'scheduling-contacts' ? ' active' : ''}`}
                onClick={() => openTab('scheduling-contacts')}
              >
                <CalendarUsersIcon />
                <span>Your Circle</span>
              </button>
              {organisationsEnabled === true && (
                <button
                  className={`nav-item${activeTab === 'organisation' ? ' active' : ''}`}
                  onClick={() => openTab('organisation')}
                >
                  <OrganisationIcon />
                  <span>Organisation</span>
                </button>
              )}
            </nav>
          </aside>

          <main className='settings-content'>
            {activeTab === 'plan-and-topups' && renderPlanAndTopUps()}
            {activeTab === 'purchase-pot' && (
              <PurchasePotSection assistantName={paName} />
            )}
            {activeTab === 'your-details' && (
              <>
                <YourDetailsSection />
                <TravelTimezoneSection />
              </>
            )}
            {activeTab === 'security' && <SecuritySection />}
            {activeTab === 'connected-accounts' && <ConnectedAccountsSection />}
            {activeTab === 'inbox-manager' && (
              <div className='settings-section'>
                <h2 className='section-title'>Inbox Manager</h2>
                <InboxManagementPage hideHeading />
              </div>
            )}
            {activeTab === 'daily-briefing' && (
              <>
                <DailyBriefingSection />
                <EveningSweepSection />
              </>
            )}
            {activeTab === 'scheduling-contacts' && (
              <SchedulingContactsSection assistantName={paName} />
            )}
            {activeTab === 'organisation' && organisationsEnabled === true && (
              <OrganisationSection />
            )}
          </main>
        </div>
      )}

      <CancelPlanModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelError(null);
        }}
        onConfirm={handleCancelPlan}
        resetsAt={
          v3Overview
            ? (v3Overview.subscription?.renewsAt ?? null)
            : (legacyOverview?.resetsAt ?? null)
        }
        billingModel={v3Overview ? 'v3' : 'credits'}
        isSubmitting={cancellingPlan}
        error={cancelError}
      />

      <TopUpModal
        isOpen={topUpModalProduct !== null}
        onClose={() => {
          setTopUpModalProduct(null);
          setTopUpError(null);
        }}
        onConfirm={handleTopUpConfirm}
        topUp={topUpModalProduct}
        cardInfo={legacyOverview?.cardInfo ?? null}
        isSubmitting={purchasingTopUp !== null}
        error={topUpError}
        offerAutoTopup={!autoTopupEnabled}
      />
    </div>
  );
};

export default Settings;
